import React from "react"
import { graphql } from "gatsby"
import Layout from "../components/layout"
import SEO from "../components/seo"
import PageHero from "../components/pageHero"
import Breadcrumbs from "../components/breadcrumbs"
import { getPagePath } from "../utils/pathUtils"
import NavLink from "../components/navLink"
import { Box, Container, Grid, Heading, Text } from "theme-ui"
import ProductThumb from "../components/productThumb"
import { LangContext, languages } from "../context/langContext"
import SubCategoryThumb from "../components/subCategoryThumb"
import { StructuredText } from "react-datocms"
import { HelmetDatoCms } from "gatsby-source-datocms"
import {
  isHeading,
  isParagraph,
  isList,
  renderRule,
} from "datocms-structured-text-utils"
import Link from "../components/link"
import Canonical from "../components/canonical"
import Hreflang from "../components/hreflang"

const Category = ({
  data: { gatsbySite, category, subCategories, products, categories },
  location,
}) => {
  // console.log(category)
  categories.nodes.map(category => {
    category.treeChildren.sort((a, b) => a.position - b.position)
    category.treeChildren.map(category => {
      if (category.treeChildren.length > 0) {
        category.treeChildren.sort((a, b) => a.position - b.position)
      }
    })
  })
  const i18nPaths = category._allSlugLocales.map(locale => {
    return {
      locale: locale.locale,
      value: getPagePath(category, locale.locale),
    }
  })

  return (
    <LangContext.Provider value={languages[category.locale]}>
      <LangContext.Consumer>
        {t => (
          <Layout
            locale={category.locale}
            location={location}
            i18nPaths={i18nPaths}
          >
            <Canonical
              siteUrl={gatsbySite.siteMetadata.siteUrl}
              paths={i18nPaths}
            />
            <Hreflang
              siteUrl={gatsbySite.siteMetadata.siteUrl}
              paths={i18nPaths}
            />
            <HelmetDatoCms seo={category.seoMetaTags}>
              <html lang={category.locale} />
            </HelmetDatoCms>
            <PageHero
              title={category.name}
              image={category.heroImage}
              locale={category.locale}
            >
              <Breadcrumbs page={category} />
            </PageHero>
            <Container mt={5}>
              <Grid gap={6} columns={[1, "1fr 3fr"]}>
                {!category.root && (
                  <div>
                    <Heading as="h3" variant="displaySmall" mb={3}>
                      {t.categories}
                    </Heading>
                    <List>
                      {categories.nodes.map(category => (
                        <li>
                          <Item>
                            <NavLink
                              variant="sidebar"
                              to={getPagePath(category)}
                            >
                              {category.shortName || category.name}
                            </NavLink>
                          </Item>
                          {category.treeChildren.length > 0 && (
                            <List>
                              {category.treeChildren.map(category => (
                                <li>
                                  <NavLink
                                    variant="sidebarItem"
                                    to={getPagePath(category)}
                                  >
                                    {category.shortName || category.name}
                                  </NavLink>
                                </li>
                              ))}
                            </List>
                          )}
                        </li>
                      ))}
                    </List>
                  </div>
                )}
                <Grid gap={4} columns={[1, 1, 2]}>
                  {products.nodes.map(product => (
                    <ProductThumb product={product} key={product.id} />
                  ))}
                </Grid>
              </Grid>
            </Container>
            <Container>
              <Grid columns={[1, 2, 2, 3]} gap={[5]}>
                {subCategories.nodes.map(subcategory => (
                  <SubCategoryThumb subcategory={subcategory} />
                ))}
              </Grid>
              <Box sx={{ mt: 6 }}>
                {category.body.value && (
                  <StructuredText
                    data={category.body}
                    renderLinkToRecord={({ record }) => {
                      switch (record.__typename) {
                        case "DatoCmsProductCategory":
                          return (
                            <Link to={getPagePath(record, record.locale)}>
                              {record.name}
                            </Link>
                          )
                        default:
                          return null
                      }
                    }}
                    customRules={[
                      renderRule(
                        isHeading,
                        ({ adapter: { renderNode }, node, children, key }) => {
                          return renderNode(
                            () => {
                              return (
                                <Text
                                  as={`h${node.level}`}
                                  variant={`h${node.level}`}
                                  color={"primary"}
                                  mb={3}
                                >
                                  {children}
                                </Text>
                              )
                            },
                            { key },
                            children
                          )
                        }
                      ),
                      renderRule(
                        isParagraph,
                        ({ adapter: { renderNode }, node, children, key }) => {
                          return renderNode(
                            () => {
                              return (
                                <Text as="p" mb={3}>
                                  {children}
                                </Text>
                              )
                            },
                            { key },
                            children
                          )
                        }
                      ),
                      renderRule(
                        isList,
                        ({ adapter: { renderNode }, node, children, key }) => {
                          return renderNode(
                            () => {
                              return (
                                <Box mt={4}>
                                  <Box
                                    as={node.style === "numbered" ? "ol" : "ul"}
                                    mb={3}
                                    sx={{
                                      color: "dark",
                                      listStyle: "none",
                                      columns: [1, 1, 1, 2],
                                      margin: 0,
                                      padding: 0,
                                      li: {
                                        display: "flex",
                                        counterIncrement: "inst",
                                        mb: 3,
                                        alignItems: "center",
                                        "&::before": {
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          content: "counter(inst)",
                                          marginRight: 4,
                                          width: "2rem",
                                          height: "2rem",
                                          position: "relative",
                                          lineHeight: "body",
                                          backgroundColor: "dark",
                                          color: "light",
                                          borderRadius: "full",
                                        },
                                        p: {
                                          mb: 0,
                                        },
                                      },
                                    }}
                                  >
                                    {children}
                                  </Box>
                                </Box>
                              )
                            },
                            { key },
                            children
                          )
                        }
                      ),
                    ]}
                  />
                )}
              </Box>
            </Container>
          </Layout>
        )}
      </LangContext.Consumer>
    </LangContext.Provider>
  )
}

const List = props => (
  <Box
    {...props}
    as="ul"
    sx={{ listStyle: "none", margin: 0, padding: 0, marginBottom: 3 }}
  />
)

const Item = props => (
  <Box
    {...props}
    as="span"
    sx={{
      borderBottom: "1px solid",
      borderColor: "dark",
      display: "block",
      pb: 2,
      mb: 3,
    }}
  />
)

export default Category

export const query = graphql`
  query ProductCategoryQuery($id: String!, $locale: String!) {
    gatsbySite: site {
      siteMetadata {
        siteUrl
      }
    }
    category: datoCmsProductCategory(id: { eq: $id }) {
      id
      name
      shortName
      root
      locale
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      ...AllProductCategorySlugLocales
      heroImage {
        fluid(maxWidth: 1400) {
          ...GatsbyDatoCmsFluid
        }
      }
      image {
        fluid(maxWidth: 700) {
          ...GatsbyDatoCmsFluid
        }
      }
      treeParent {
        name
        slug
        locale
        ...AllProductCategorySlugLocales
        root
        treeParent {
          name
          slug
          locale
          ...AllProductCategorySlugLocales
          root
        }
      }
      body {
        links {
          __typename
          ... on DatoCmsProductCategory {
            id: originalId
            name
            shortName
            locale
            slug
            ...AllProductCategorySlugLocales
            treeParent {
              name
              shortName
              slug
              locale
              ...AllProductCategorySlugLocales
              root
              treeParent {
                name
                shortName
                slug
                locale
                ...AllProductCategorySlugLocales
                root
              }
            }
            model {
              apiKey
            }
          }
        }
        value
      }
      model {
        apiKey
      }
    }
    subCategories: allDatoCmsProductCategory(
      filter: { treeParent: { id: { eq: $id } } }
      sort: { fields: position, order: ASC }
    ) {
      nodes {
        id
        name
        shortName
        slug
        locale
        ...AllProductCategorySlugLocales
        image {
          fluid(maxWidth: 700) {
            ...GatsbyDatoCmsFluid
          }
        }
        treeParent {
          name
          slug
          shortName
          locale
          ...AllProductCategorySlugLocales
          root
        }
      }
    }
    products: allDatoCmsProduct(
      filter: { category: { id: { eq: $id } } }
      sort: { fields: position, order: ASC }
    ) {
      nodes {
        name
        id
        slug
        locale
        ...allProductSlugLocales
        description
        summary
        image {
          fluid(maxWidth: 700) {
            ...GatsbyDatoCmsFluid
          }
        }
        category {
          slug
          root
          locale
          ...AllProductCategorySlugLocales
          treeParent {
            slug
            root
            locale
            ...AllProductCategorySlugLocales
            treeParent {
              slug
              root
              locale
              ...AllProductCategorySlugLocales
            }
          }
        }
      }
    }
    categories: allDatoCmsProductCategory(
      filter: { root: { eq: true }, locale: { eq: $locale } }
      sort: { fields: position, order: ASC }
    ) {
      nodes {
        slug
        id
        locale
        name
        shortName
        ...AllProductCategorySlugLocales
        root
        treeChildren {
          slug
          name
          locale
          shortName
          ...AllProductCategorySlugLocales
          root
          position
          treeParent {
            slug
            name
            locale
            ...AllProductCategorySlugLocales
            root
          }
          treeChildren {
            name
            slug
            locale
            shortName
            ...AllProductCategorySlugLocales
            root
            position
            treeParent {
              slug
              name
              locale
              ...AllProductCategorySlugLocales
              root
            }
          }
        }
      }
    }
  }

  fragment AllProductCategorySlugLocales on DatoCmsProductCategory {
    _allSlugLocales {
      value
      locale
    }
  }
`
